import React, { useEffect, useState } from 'react';
import { ArrowRight, BarChart3, CheckCircle2, Eye, FileSpreadsheet, Trash2, UploadCloud, AlertCircle } from 'lucide-react';
import Button from '../components/ui/Button';
import FileDropzone from '../components/upload/FileDropzone';
import UploadProgress from '../components/upload/UploadProgress';
import {
  getBillingSummary,
  getSubscriptionErrorMessage,
  recordUsage,
  type BillingSummary,
} from '../services/subscription';
import {
  deleteSavedDataset,
  getSavedDatasets,
  getStoredDataset,
  parseBusinessFile,
  saveUploadedDataset,
  selectSavedDataset,
} from '../services/analytics';

const UploadPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [savedDatasets, setSavedDatasets] = useState(getSavedDatasets);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const currentDataset = getStoredDataset();

  useEffect(() => {
    let isMounted = true;

    getBillingSummary()
      .then((summary) => {
        if (isMounted) setBillingSummary(summary);
      })
      .catch(() => {
        if (isMounted) setBillingSummary(null);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setError('');
    setProgress(15);

    try {
      const parsedDataset = await parseBusinessFile(selectedFile);
      setProgress(72);
      const usage = await recordUsage('upload');
      setBillingSummary((current) => current ? { ...current, usage } : current);
      setProgress(88);
      saveUploadedDataset(parsedDataset);
      setSavedDatasets(getSavedDatasets());
      setProgress(100);
      setTimeout(() => window.location.assign('/review'), 300);
    } catch (err) {
      setFile(null);
      setProgress(0);
      setError(err instanceof Error ? getSubscriptionErrorMessage(err) : 'GabMic could not read this file.');
    }
  };

  const openSavedDataset = (datasetId: string) => {
    selectSavedDataset(datasetId);
    window.location.assign('/analytics');
  };

  const removeSavedDataset = (datasetId: string) => {
    deleteSavedDataset(datasetId);
    setSavedDatasets(getSavedDatasets());
  };

  return (
    <div className="page-stack" style={{ maxWidth: '1100px' }}>
      <header>
        <h1 style={{ fontSize: '2.55rem', marginBottom: '0.65rem' }}>Upload your business data</h1>
        <p className="page-kicker">Add a CSV or Excel file. GabMic will detect the columns and prepare your analytics.</p>
      </header>

      <div className="premium-card" style={{ padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <strong style={{ display: 'block' }}>{billingSummary?.customer.planName ?? 'GabMic'} plan</strong>
          <p style={{ margin: 0, fontSize: '0.9rem' }}>
            {billingSummary
              ? `${formatRemainingUploads(billingSummary.usage.remaining.uploads)} remaining this month.`
              : 'Sign in to enforce your monthly upload allowance.'}
          </p>
        </div>
        <a href="/pricing">
          <Button variant={billingSummary?.customer.effectivePlanId === 'free' ? 'primary' : 'outline'} size="sm">
            View pricing
          </Button>
        </a>
      </div>

      {error && (
        <div className="premium-card" style={{ padding: '1rem', borderColor: 'var(--status-danger)', color: 'var(--status-danger)', display: 'flex', gap: '0.7rem', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', gap: '0.7rem', alignItems: 'center' }}>
            <AlertCircle size={18} />
            {error}
          </span>
          {error.toLowerCase().includes('limit') && (
            <a href="/pricing">
              <Button variant="outline" size="sm">Upgrade</Button>
            </a>
          )}
        </div>
      )}
      
      {!file ? (
        <FileDropzone onFileSelect={handleFileSelect} />
      ) : (
        <UploadProgress progress={progress} fileName={file.name} />
      )}

      <div className="premium-card" style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '1rem' }}>
        {[
          { icon: UploadCloud, title: 'Upload file', text: 'Add your CSV or Excel file' },
          { icon: Eye, title: 'Review columns', text: 'Confirm detected fields' },
          { icon: BarChart3, title: 'View analytics', text: 'Open charts and summaries' },
        ].map((step, index) => {
          const Icon = step.icon;
          return (
            <div key={step.title} style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', padding: '0.5rem' }}>
              <span className="icon-tile" style={{ color: 'var(--gabmic-orange)' }}>
                {index + 1}
              </span>
              <div>
                <strong>{step.title}</strong>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>{step.text}</p>
              </div>
              <Icon size={18} style={{ marginLeft: 'auto', color: 'var(--text-muted)' }} />
            </div>
          );
        })}
      </div>

      {savedDatasets.length > 0 && (
        <div id="saved-datasets" className="premium-card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center' }}>
            <div>
              <h3 className="section-title">Saved datasets</h3>
              <p style={{ margin: 0, fontSize: '0.88rem' }}>Reopen recent uploads without selecting the file again.</p>
            </div>
            <span className="soft-chip" style={{ minHeight: '32px', padding: '0.35rem 0.65rem' }}>
              {savedDatasets.length} saved
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {savedDatasets.map((dataset) => {
              const isCurrent = currentDataset?.id === dataset.id;
              return (
                <div
                  key={dataset.id}
                  style={{
                    padding: '1rem 1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '1rem',
                    borderBottom: '1px solid var(--border-color)',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', minWidth: 0 }}>
                    <span className="icon-tile">
                      <FileSpreadsheet size={19} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dataset.fileName}</strong>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', color: 'var(--text-secondary)', fontSize: '0.86rem', flexWrap: 'wrap' }}>
                        {dataset.rows.length.toLocaleString()} rows
                        <span className="status-dot" />
                        {new Date(dataset.uploadedAt).toLocaleDateString()}
                        {isCurrent && <span className="orange-text">Current</span>}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                    <Button variant="outline" size="sm" onClick={() => openSavedDataset(dataset.id)}>
                      <CheckCircle2 size={16} />
                      Open
                      <ArrowRight size={16} />
                    </Button>
                    <button className="icon-button" type="button" aria-label={`Remove ${dataset.fileName}`} title="Remove saved dataset" onClick={() => removeSavedDataset(dataset.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const formatRemainingUploads = (remaining: number | null) => {
  if (remaining === null) return 'Custom uploads';
  return `${remaining.toLocaleString()} upload${remaining === 1 ? '' : 's'}`;
};

export default UploadPage;
